/* eslint-disable react/jsx-key */
import React, { useContext, useEffect, useState } from 'react';
import {
  getProjectType,
  hasValue,
  ProjectTypes,
} from '@r-and-a-shared-ui/utils';
import { FormField, FormFieldProps } from '../fields';
import stylesPP from './scss/FormStepPP.module.scss';
import stylesSSO from './scss/FormStepSSO.module.scss';

import StepsCounter from '../StepsCounter/StepsCounter';
import { FormCTA, FormCTAProps } from '../FormCTA/FormCTA';
import RichTextWithAsset, {
  RichTextWithAssetProps,
} from '../RichTextWithAsset/RichTextWithAsset';
import { FormHeaderProps } from '../FormHeader/FormHeader';
import {
  FormContentContext,
  FormContentContextType,
} from '../context/FormContentContext';
import {
  AssetProps,
  RichTextWithAssetContainer,
  RichTextWithAssetContainerProps,
} from '@r-and-a-shared-ui/common-ui';
import { DictionaryWithLinkedItemProps } from '../types';
import { useFormContext } from 'react-hook-form';
import { shouldHideFieldByDependency } from '../helpers/field-visibility-helper';

export interface FormStepProps {
  key?: string;
  fieldsData?: FormFieldProps[];
  ctasData?: FormCTAProps[];
  richTextItems?: RichTextWithAssetProps[] | RichTextWithAssetContainerProps[];
  showStepCounter: boolean;
  customHeader?: FormHeaderProps;
  audio?: AssetProps;
  isMembership?: boolean;
  toEmail?: string;
  fromEmail?: string;
  subject?: string;
  emailKey?: string;
  withModalAndPreview?: boolean;
  displayHideStepBasedOnFieldValue?: DictionaryWithLinkedItemProps;
}

export function FormStep(props: FormStepProps) {
  const {
    fieldsData,
    ctasData,
    richTextItems,
    showStepCounter,
    isMembership,
    toEmail,
    fromEmail,
    subject,
    emailKey,
    withModalAndPreview,
  } = props;
  const { formContentProps, currentFormStep } = useContext(
    FormContentContext,
  ) as FormContentContextType;

  const stepsLength =
    formContentProps.steps?.filter(step => step.showStepCounter).length || 0;

  const projectType = getProjectType();
  const isPlayerPortals = projectType === ProjectTypes.PlayerPortals;
  const styles = isPlayerPortals ? stylesPP : stylesSSO;

  const [submissionSuccess, setSubmissionSuccess] = useState<boolean>(false);
  const { watch } = useFormContext();
  const watchedValues = watch();

  useEffect(() => {
    if (!isMembership && !isPlayerPortals) return;

    let offset = isPlayerPortals ? 100 : 0;
    window.scrollTo({ top: offset, left: 0, behavior: 'smooth' });
  }, []);

  const renderFormFields = (
    fieldsProps: FormFieldProps[] | undefined,
  ): React.JSX.Element[] | null => {
    if (!hasValue(fieldsProps) || typeof fieldsProps === 'undefined') {
      return null;
    }

    return fieldsProps.map(fieldProps => {
      const hideByFieldRule = shouldHideFieldByDependency(
        fieldProps,
        watchedValues,
      );

      if (
        !hasValue(fieldProps.fieldType) ||
        (fieldProps.isDisabled && withModalAndPreview)
      ) {
        return <React.Fragment key={fieldProps?.key} />;
      }
      return (
        <FormField
          {...fieldProps}
          key={fieldProps?.key}
          isHidden={fieldProps.isHidden || hideByFieldRule}
          isMembership={isMembership}
          isSubmittedSuccessfully={submissionSuccess}
        />
      );
    });
  };

  const renderFormRichTextItems = (
    richTextItems:
      | RichTextWithAssetProps[]
      | RichTextWithAssetContainerProps[]
      | undefined,
  ): React.JSX.Element[] | null => {
    if (!hasValue(richTextItems) || typeof richTextItems === 'undefined') {
      return null;
    }

    return richTextItems.map(richTextItem => {
      if (
        hasValue(
          (richTextItem as RichTextWithAssetContainerProps)
            .richTextWithAssetElements,
        )
      ) {
        return (
          <RichTextWithAssetContainer
            {...(richTextItem as RichTextWithAssetContainerProps)}
            key={(richTextItem as RichTextWithAssetProps)?.key}
          />
        );
      }

      return (
        <RichTextWithAsset
          {...(richTextItem as RichTextWithAssetProps)}
          key={(richTextItem as RichTextWithAssetProps)?.key}
        />
      );
    });
  };

  const renderFormCTAs = (
    ctasProps: FormCTAProps[] | undefined,
  ): React.JSX.Element[] | null => {
    if (!hasValue(ctasProps) || typeof ctasProps === 'undefined') {
      return null;
    }

    return ctasProps.map(ctaProps => {
      return (
        <FormCTA
          {...ctaProps} // add here the customClientCallback
          key={ctaProps?.key}
          isMembership={isMembership}
          onSubmissionSuccess={() => setSubmissionSuccess(true)}
          toEmail={toEmail}
          fromEmail={fromEmail}
          subject={subject}
          emailKey={emailKey}
        />
      );
    });
  };

  return (
    <div className={`${styles['form-step']}`}>
      <div className={`${styles['form-step__wrapper']}`}>
        <div className={`${styles['form-step__content']}`}>
          {renderFormFields(fieldsData)}
        </div>
        {richTextItems && (
          <div className={`${styles['form-step__content']}`}>
            {renderFormRichTextItems(richTextItems)}
          </div>
        )}

        {stepsLength > 1 && showStepCounter && (
          <StepsCounter count={stepsLength} current={currentFormStep} />
        )}
        <div className={`${styles['form-step__buttons']}`}>
          {renderFormCTAs(ctasData)}
        </div>
      </div>
    </div>
  );
}

export default FormStep;
